import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { venueService } from '@/services/VenueService'
import { eventService } from '@/services/EventService'
import type { PlanImpactDto } from '@lib/dto/plan-impact.dto'
import { inventoryKeys, marketingKeys, venueKeys, venuePageKeys } from '@/hooks/queryKeys'
import { refreshPeople } from '@/hooks/useUsers'
import type {
  TimezoneListDto,
  VenueDto,
  VenueListDto,
  VenueManagerListDto,
  VenueSpaceDto,
} from '@lib/dto/venue.dto'
import type {
  CreateEventTrackInput,
  CreateSpaceVersionInput,
  CreateVenueInput,
  CreateVenueSpaceInput,
  UpdateEventTrackInput,
  UpdateSpaceVersionInput,
  UpdateVenueInput,
  UpdateVenueScheduleInput,
  UpdateVenueSpaceInput,
} from '@lib/input/venue.input'
import type { ApplySpaceImportInput, PreviewSpaceImportInput } from '@lib/input/space-import.input'
import type { SpaceImportApplyResultDto, SpaceImportPreviewDto } from '@lib/dto/space-import.dto'
import type {
  AddSpacePhotoInput,
  ReorderSpacePhotosInput,
  UpdateSpacePhotoInput,
} from '@lib/input/space-photo.input'
import type { SetCoverImageInput } from '@lib/input/cover-image.input'

/**
 * Load the venues visible to the current user (tenant-scoped by the API).
 * Returns the raw query result so callers drive loading / empty / error states.
 */
export function useVenues(page?: number): UseQueryResult<VenueListDto> {
  return useQuery({
    queryKey: venueKeys.list(page),
    queryFn: () => venueService.list(page),
    staleTime: 30_000,
    // The previous page's rows stay on screen while the next one loads, so
    // paging moves the list rather than emptying it and filling it again.
    placeholderData: (previous) => previous,
  })
}

/** Load a single venue with its spaces (tenant-scoped by the API). */
export function useVenue(id: string): UseQueryResult<VenueDto> {
  return useQuery({
    queryKey: venueKeys.detail(id),
    queryFn: () => venueService.get(id),
    enabled: id !== '',
    staleTime: 30_000,
  })
}

/** Create a venue under the current tenant, refreshing the list on success. */
export function useCreateVenue(): UseMutationResult<VenueDto, Error, CreateVenueInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateVenueInput) => venueService.create(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: venueKeys.lists() }),
  })
}

/** Rename a venue / edit its address, refreshing the list and its detail on success. */
export function useUpdateVenue(): UseMutationResult<VenueDto, Error, { id: string; input: UpdateVenueInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateVenueInput }) => venueService.update(vars.id, vars.input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.id) })
      // A new name moves the public page's address; a new address may find or lose its place.
      void queryClient.invalidateQueries({ queryKey: venuePageKeys.settings(vars.id) })
    },
  })
}

/**
 * Set the venue's clock and usual evening.
 *
 * Its own mutation because it is its own route, for the same reason the cover
 * photograph has one: a venue admin may set how their venue runs without being
 * able to rename it.
 */
export function useUpdateVenueSchedule(): UseMutationResult<
  VenueDto,
  Error,
  { id: string; input: UpdateVenueScheduleInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateVenueScheduleInput }) =>
      venueService.updateSchedule(vars.id, vars.input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.id) })
    },
  })
}

/**
 * The time zones the API will accept, for the schedule form's dropdown.
 *
 * Served rather than computed in the browser (see `timezoneListDtoSchema`), and
 * cached for the session: the list changes when the API is deployed, not while
 * somebody is filling in a form.
 */
export function useVenueTimezones(): UseQueryResult<TimezoneListDto> {
  return useQuery({
    queryKey: venueKeys.timezones(),
    queryFn: () => venueService.timezones(),
    staleTime: Infinity,
  })
}

/**
 * Set or clear a venue's cover photograph. Its own mutation because it is its own
 * route: a venue admin may change the photograph without being able to rename
 * the venue.
 */
export function useSetVenueCover(): UseMutationResult<
  VenueDto,
  Error,
  { id: string } & SetCoverImageInput
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & SetCoverImageInput) =>
      venueService.setCover(id, input),
    onSuccess: (_data, vars) => {
      // The index shows covers, so it goes stale too.
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.id) })
      // The public page needs a photo, so whether it can show may have changed.
      void queryClient.invalidateQueries({ queryKey: venuePageKeys.settings(vars.id) })
    },
  })
}

/**
 * Soft-delete a venue. Refreshes the list and drops the detail cache so a
 * follow-up navigate doesn't flash stale data.
 */
export function useDeleteVenue(): UseMutationResult<void, Error, { id: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string }) => venueService.delete(vars.id),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.removeQueries({ queryKey: venueKeys.detail(vars.id) })
    },
  })
}

/** Add a space to a venue, refreshing the list and the venue detail on success. */
export function useCreateSpace(): UseMutationResult<VenueDto, Error, { venueId: string; input: CreateVenueSpaceInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; input: CreateVenueSpaceInput }) => venueService.createSpace(vars.venueId, vars.input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.venueId) })
    },
  })
}

/** Delete a space. Fails with 409 when any event phase still uses it. */
export function useDeleteSpace(): UseMutationResult<void, Error, { venueId: string; spaceId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; spaceId: string }) => venueService.deleteSpace(vars.venueId, vars.spaceId),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.venueId) })
      void queryClient.invalidateQueries({ queryKey: [...venueKeys.all, 'version'] })
      // Stock locked to the room goes with it.
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.venue(vars.venueId) })
    },
  })
}

/** Update a venue space (name / background), refreshing the list and venue detail. */
export function useUpdateSpace(): UseMutationResult<
  VenueSpaceDto,
  Error,
  { venueId: string; spaceId: string; input: UpdateVenueSpaceInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; spaceId: string; input: UpdateVenueSpaceInput }) =>
      venueService.updateSpace(vars.venueId, vars.spaceId, vars.input),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: venueKeys.detail(vars.venueId) })
    },
  })
}

// ---- space photographs ------------------------------------------------------

/**
 * The photograph mutations, which all answer with the whole space.
 *
 * Returning the space rather than the photograph means the caller never has to
 * merge a list by hand — order, ids and signed URLs all arrive together, which
 * matters because a reorder changes every row's sortOrder at once.
 */
type PhotoVars = { venueId: string; spaceId: string }

function invalidateSpace(queryClient: ReturnType<typeof useQueryClient>, venueId: string): void {
  void queryClient.invalidateQueries({ queryKey: venueKeys.detail(venueId) })
  void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
}

export function useAddSpacePhoto(): UseMutationResult<
  VenueSpaceDto,
  Error,
  PhotoVars & { input: AddSpacePhotoInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: PhotoVars & { input: AddSpacePhotoInput }) =>
      venueService.addSpacePhoto(v.venueId, v.spaceId, v.input),
    onSuccess: (_d, v) => invalidateSpace(queryClient, v.venueId),
  })
}

export function useUpdateSpacePhoto(): UseMutationResult<
  VenueSpaceDto,
  Error,
  PhotoVars & { photoId: string; input: UpdateSpacePhotoInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: PhotoVars & { photoId: string; input: UpdateSpacePhotoInput }) =>
      venueService.updateSpacePhoto(v.venueId, v.spaceId, v.photoId, v.input),
    onSuccess: (_d, v) => invalidateSpace(queryClient, v.venueId),
  })
}

export function useDeleteSpacePhoto(): UseMutationResult<VenueSpaceDto, Error, PhotoVars & { photoId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: PhotoVars & { photoId: string }) =>
      venueService.deleteSpacePhoto(v.venueId, v.spaceId, v.photoId),
    onSuccess: (_d, v) => invalidateSpace(queryClient, v.venueId),
  })
}

export function useReorderSpacePhotos(): UseMutationResult<
  VenueSpaceDto,
  Error,
  PhotoVars & { input: ReorderSpacePhotosInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (v: PhotoVars & { input: ReorderSpacePhotosInput }) =>
      venueService.reorderSpacePhotos(v.venueId, v.spaceId, v.input),
    onSuccess: (_d, v) => invalidateSpace(queryClient, v.venueId),
  })
}

/** Load one specific version of a space (its full settings), for editing it. */
export function useSpaceVersion(
  venueId: string,
  spaceId: string,
  versionId: string,
  enabled: boolean,
): UseQueryResult<VenueSpaceDto> {
  return useQuery({
    queryKey: venueKeys.version(venueId, spaceId, versionId),
    queryFn: () => venueService.getSpaceVersion(venueId, spaceId, versionId),
    enabled: enabled && versionId !== '',
    staleTime: 30_000,
  })
}

/** Invalidate everything that reflects a space's versions after an authoring change. */
function invalidateVenueAndVersions(queryClient: ReturnType<typeof useQueryClient>, venueId: string): void {
  void queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
  void queryClient.invalidateQueries({ queryKey: venueKeys.detail(venueId) })
  void queryClient.invalidateQueries({ queryKey: [...venueKeys.all, 'version'] })
}

/** Create a remodel (a new version) of a space. */
export function useCreateSpaceVersion(): UseMutationResult<
  VenueSpaceDto,
  Error,
  { venueId: string; spaceId: string; input: CreateSpaceVersionInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; spaceId: string; input: CreateSpaceVersionInput }) =>
      venueService.createSpaceVersion(vars.venueId, vars.spaceId, vars.input),
    onSuccess: (_data, vars) => invalidateVenueAndVersions(queryClient, vars.venueId),
  })
}

/** Update one version's settings, label, or effective date. */
export function useUpdateSpaceVersion(): UseMutationResult<
  VenueSpaceDto,
  Error,
  { venueId: string; spaceId: string; versionId: string; input: UpdateSpaceVersionInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; spaceId: string; versionId: string; input: UpdateSpaceVersionInput }) =>
      venueService.updateSpaceVersion(vars.venueId, vars.spaceId, vars.versionId, vars.input),
    onSuccess: (_data, vars) => invalidateVenueAndVersions(queryClient, vars.venueId),
  })
}

/** Delete a remodel version. */
export function useDeleteSpaceVersion(): UseMutationResult<
  void,
  Error,
  { venueId: string; spaceId: string; versionId: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; spaceId: string; versionId: string }) =>
      venueService.deleteSpaceVersion(vars.venueId, vars.spaceId, vars.versionId),
    onSuccess: (_data, vars) => invalidateVenueAndVersions(queryClient, vars.venueId),
  })
}

/** Dry-run a legacy SVG floorplan import for a space. */
export function usePreviewSpaceImport(
  venueId: string,
  spaceId: string,
): UseMutationResult<SpaceImportPreviewDto, Error, PreviewSpaceImportInput> {
  return useMutation({
    mutationFn: (input: PreviewSpaceImportInput) => venueService.previewSpaceImport(venueId, spaceId, input),
  })
}

/** Apply a legacy SVG floorplan import; refreshes venue + version caches. */
export function useApplySpaceImport(
  venueId: string,
  spaceId: string,
): UseMutationResult<SpaceImportApplyResultDto, Error, ApplySpaceImportInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ApplySpaceImportInput) => venueService.applySpaceImport(venueId, spaceId, input),
    onSuccess: async (data) => {
      // Await invalidation so the space editor remounts against fresh zones/dims.
      await queryClient.invalidateQueries({ queryKey: venueKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: venueKeys.detail(venueId) })
      await queryClient.invalidateQueries({ queryKey: [...venueKeys.all, 'version'] })
      if (data.space.versionId) {
        await queryClient.invalidateQueries({
          queryKey: venueKeys.version(venueId, spaceId, data.space.versionId),
        })
      }
    },
  })
}

/** Create an event track in a venue. */
export function useCreateTrack(): UseMutationResult<VenueDto, Error, { venueId: string; input: CreateEventTrackInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; input: CreateEventTrackInput }) =>
      venueService.createTrack(vars.venueId, vars.input),
    onSuccess: (_data, vars) => invalidateVenueAndVersions(queryClient, vars.venueId),
  })
}

/** Update a track (rename, reorder, assign spaces). */
export function useUpdateTrack(): UseMutationResult<
  VenueDto,
  Error,
  { venueId: string; trackId: string; input: UpdateEventTrackInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; trackId: string; input: UpdateEventTrackInput }) =>
      venueService.updateTrack(vars.venueId, vars.trackId, vars.input),
    onSuccess: (_data, vars) => invalidateVenueAndVersions(queryClient, vars.venueId),
  })
}

/** Delete a track. */
export function useDeleteTrack(): UseMutationResult<void, Error, { venueId: string; trackId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; trackId: string }) => venueService.deleteTrack(vars.venueId, vars.trackId),
    onSuccess: (_data, vars) => {
      invalidateVenueAndVersions(queryClient, vars.venueId)
      // The track's pricing page goes with it.
      void queryClient.invalidateQueries({ queryKey: marketingKeys.forVenue(vars.venueId) })
    },
  })
}

/** The venue admins assigned to a venue (org-admin only). */
export function useVenueManagers(venueId: string): UseQueryResult<VenueManagerListDto> {
  return useQuery({
    queryKey: venueKeys.managers(venueId),
    queryFn: () => venueService.listManagers(venueId),
    enabled: venueId !== '',
    staleTime: 30_000,
  })
}

/** Give a member of staff this venue, refreshing who works where on success. */
export function useAssignVenueManager(): UseMutationResult<
  VenueManagerListDto,
  Error,
  { venueId: string; userId: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; userId: string }) => venueService.assignManager(vars.venueId, vars.userId),
    // The venue's staff, and the person's venues wherever else they're read.
    onSuccess: () => refreshPeople(queryClient),
  })
}

/** Take this venue off a member of staff, refreshing who works where on success. */
export function useUnassignVenueManager(): UseMutationResult<void, Error, { venueId: string; userId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string; userId: string }) => venueService.unassignManager(vars.venueId, vars.userId),
    onSuccess: () => refreshPeople(queryClient),
  })
}

/**
 * The unconfirmed plans that follow one version of a room — what a drawing on
 * it reaches. Nested under the version's key, so creating, editing or deleting
 * a version refreshes it along with the version itself.
 */
export function useRoomFollowers(
  venueId: string,
  spaceId: string,
  versionId: string,
  enabled: boolean,
): UseQueryResult<PlanImpactDto, Error> {
  return useQuery({
    queryKey: venueKeys.followers(venueId, spaceId, versionId),
    queryFn: () => eventService.roomImpact({ venueId, venueSpaceId: spaceId, versionId, change: { kind: 'redraw' } }),
    enabled: enabled && venueId !== '' && spaceId !== '' && versionId !== '',
    staleTime: 60_000,
  })
}
